import React, { useEffect, useRef, useState } from "react";
import { Play, Copy, UploadCloud, CheckCircle2, XCircle, AlertTriangle, Loader2, MinusCircle, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import useGreenScreen from "../lib/useGreenScreen";

/**
 * GsDiagLab — Green Screen pipeline diagnostic lab (/gs-lab).
 *
 * Runs 9 isolated stage tests on the REAL device to pinpoint exactly
 * where the compositor pipeline breaks:
 *   1 camera         getUserMedia + visible <video> renders frames
 *   2 hidden-video   off-screen <video> (hook's technique) decodes frames
 *   3 mediapipe      CDN script + segmenter construct
 *   4 mask           segmenter emits masks
 *   5 composite      blur+cutout drawn on a VISIBLE canvas (eyeball check)
 *   6 cap-detached   captureStream from a DETACHED canvas → <video>  [production path]
 *   7 cap-attached   captureStream from the VISIBLE canvas → <video> [fix candidate]
 *   8 resize         canvas resized AFTER captureStream — does the stream survive? [known WebKit bug probe]
 *   9 hook           the real useGreenScreen hook end-to-end
 *
 * Produces a JSON report the user can copy or upload (POST /api/greenscreen/diag).
 */

const STAGE_DEFS = [
  { key: "cam",    label: "1 · Camera access" },
  { key: "hidden", label: "2 · Hidden video decode" },
  { key: "mp",     label: "3 · MediaPipe AI load" },
  { key: "mask",   label: "4 · AI mask production" },
  { key: "comp",   label: "5 · Canvas composite (look below — do you see blur + cutout?)" },
  { key: "capdet", label: "6 · captureStream · detached canvas (production path)" },
  { key: "capatt", label: "7 · captureStream · visible canvas (fix candidate)" },
  { key: "resize", label: "8 · Canvas resize during capture (WebKit bug probe)" },
  { key: "hook",   label: "9 · Real useGreenScreen hook E2E" },
];

function freshStages() {
  const o = {};
  STAGE_DEFS.forEach((s) => { o[s.key] = { status: "pending", detail: "", data: {} }; });
  return o;
}

function samplePixels(el) {
  try {
    const c = document.createElement("canvas");
    c.width = 16; c.height = 16;
    const cx = c.getContext("2d", { willReadFrequently: true });
    cx.drawImage(el, 0, 0, 16, 16);
    const d = cx.getImageData(0, 0, 16, 16).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += d[i] + d[i + 1] + d[i + 2];
    return Math.round(sum / (16 * 16 * 3));
  } catch { return -1; }
}

function countFrames(videoEl, ms) {
  return new Promise((resolve) => {
    if (!videoEl) { resolve(0); return; }
    let frames = 0;
    let stopped = false;
    if (typeof videoEl.requestVideoFrameCallback === "function") {
      const onFrame = () => { frames++; if (!stopped) videoEl.requestVideoFrameCallback(onFrame); };
      videoEl.requestVideoFrameCallback(onFrame);
      setTimeout(() => { stopped = true; resolve(frames); }, ms);
    } else {
      let last = videoEl.currentTime;
      const iv = setInterval(() => {
        if (videoEl.currentTime !== last) { frames++; last = videoEl.currentTime; }
      }, 80);
      setTimeout(() => { clearInterval(iv); resolve(frames); }, ms);
    }
  });
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function HookProbe({ stream, videoElRef, onUpdate }) {
  const { track, ready, segStatus } = useGreenScreen({
    stream,
    enabled: true,
    background: { mode: "blur", blurLevel: "medium" },
    chroma: { enabled: true, mode: "person", color: "#00FF00", similarity: 0.55, smoothness: 0.15 },
  });
  useEffect(() => {
    onUpdate({ trackId: track?.id || null, trackState: track?.readyState || null, trackMuted: track?.muted ?? null, ready, segStatus });
    const v = videoElRef.current;
    if (v && track) {
      try { v.srcObject = new MediaStream([track]); v.play().catch(() => {}); } catch { /* ignore */ }
    }
  }, [track, ready, segStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function GsDiagLab() {
  const [stages, setStages] = useState(freshStages);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [verdicts, setVerdicts] = useState([]);
  const [reportId, setReportId] = useState(null);
  const [hookActive, setHookActive] = useState(false);
  const [camStream, setCamStream] = useState(null);

  const camVideoRef = useRef(null);
  const masterCanvasRef = useRef(null);
  const capDetVideoRef = useRef(null);
  const capAttVideoRef = useRef(null);
  const resizeCanvasRef = useRef(null);
  const hookVideoRef = useRef(null);

  const stoppedRef = useRef(false);
  const hiddenVidRef = useRef(null);
  const hiddenWrapRef = useRef(null);
  const segRef = useRef(null);
  const segBusyRef = useRef(false);
  const maskCanvasRef = useRef(null);
  const maskCountRef = useRef(0);
  const haveMaskRef = useRef(false);
  const drawCountRef = useRef(0);
  const detachedCanvasRef = useRef(null);
  const compositeRafRef = useRef(0);
  const resizeRafRef = useRef(0);
  const hookInfoRef = useRef({});
  const stagesRef = useRef(stages);

  const setStage = (key, patch) => {
    setStages((cur) => {
      const next = { ...cur, [key]: { ...cur[key], ...patch } };
      stagesRef.current = next;
      return next;
    });
  };

  const cleanupAll = () => {
    stoppedRef.current = true;
    try { cancelAnimationFrame(compositeRafRef.current); } catch {}
    try { cancelAnimationFrame(resizeRafRef.current); } catch {}
    try { segRef.current?.close?.(); } catch {}
    segRef.current = null;
    try { hiddenWrapRef.current?.remove(); } catch {}
    setCamStream((s) => { try { s?.getTracks().forEach((t) => t.stop()); } catch {} return null; });
    setHookActive(false);
  };
  useEffect(() => () => cleanupAll(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Stage implementations ----------
  const stageCamera = async () => {
    setStage("cam", { status: "running", detail: "Requesting camera…" });
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: false,
      });
      setCamStream(s);
      const v = camVideoRef.current;
      v.srcObject = s;
      await v.play().catch(() => {});
      await sleep(600);
      const frames = await countFrames(v, 2000);
      const px = samplePixels(v);
      const set = s.getVideoTracks()[0]?.getSettings?.() || {};
      const ok = frames >= 3 && v.videoWidth > 0;
      setStage("cam", {
        status: ok ? "pass" : "fail",
        detail: `${set.width || v.videoWidth}×${set.height || v.videoHeight} · ${frames} frames/2s · brightness ${px}`,
        data: { frames, px, width: set.width, height: set.height },
      });
      return ok ? s : null;
    } catch (e) {
      setStage("cam", { status: "fail", detail: `getUserMedia failed: ${e?.name || ""} ${e?.message || e}`, data: { error: String(e) } });
      return null;
    }
  };

  const stageHidden = async (stream) => {
    setStage("hidden", { status: "running", detail: "Building hidden <video>…" });
    const wrap = document.createElement("div");
    wrap.style.cssText = "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;";
    document.body.appendChild(wrap);
    hiddenWrapRef.current = wrap;
    const vid = document.createElement("video");
    vid.muted = true; vid.playsInline = true; vid.autoplay = true;
    vid.srcObject = new MediaStream([stream.getVideoTracks()[0]]);
    wrap.appendChild(vid);
    await vid.play().catch(() => {});
    hiddenVidRef.current = vid;
    await sleep(500);
    const frames = await countFrames(vid, 2000);
    const ok = frames >= 3 && vid.videoWidth > 0;
    setStage("hidden", {
      status: ok ? "pass" : "fail",
      detail: `${vid.videoWidth}×${vid.videoHeight} · ${frames} frames/2s · readyState=${vid.readyState}`,
      data: { frames, w: vid.videoWidth, h: vid.videoHeight, readyState: vid.readyState },
    });
    return ok;
  };

  const stageMediaPipe = async () => {
    setStage("mp", { status: "running", detail: "Loading MediaPipe from CDN…" });
    const t0 = performance.now();
    try {
      if (!window.SelfieSegmentation) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/selfie_segmentation.js";
          s.async = true;
          s.onload = res;
          s.onerror = () => rej(new Error("script load failed"));
          document.head.appendChild(s);
        });
      }
      const seg = new window.SelfieSegmentation({
        locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${f}`,
      });
      seg.setOptions({ modelSelection: 0, selfieMode: false });
      const mc = document.createElement("canvas");
      mc.width = 720; mc.height = 1280;
      maskCanvasRef.current = mc;
      const mctx = mc.getContext("2d");
      seg.onResults((res) => {
        segBusyRef.current = false;
        if (!res?.segmentationMask) return;
        mctx.clearRect(0, 0, mc.width, mc.height);
        mctx.drawImage(res.segmentationMask, 0, 0, mc.width, mc.height);
        maskCountRef.current++;
        haveMaskRef.current = true;
      });
      segRef.current = seg;
      const ms = Math.round(performance.now() - t0);
      setStage("mp", { status: "pass", detail: `Loaded + constructed in ${ms} ms`, data: { ms } });
      return true;
    } catch (e) {
      setStage("mp", { status: "fail", detail: `Load failed: ${e?.message || e}`, data: { error: String(e) } });
      return false;
    }
  };

  const stageMask = async () => {
    setStage("mask", { status: "running", detail: "Feeding frames to the segmenter…" });
    const vid = hiddenVidRef.current;
    const seg = segRef.current;
    maskCountRef.current = 0;
    const t0 = performance.now();
    while (performance.now() - t0 < 5000 && maskCountRef.current < 8) {
      if (!segBusyRef.current && vid.readyState >= 2) {
        segBusyRef.current = true;
        try { await seg.send({ image: vid }); } catch { segBusyRef.current = false; }
      }
      await sleep(80);
    }
    const n = maskCountRef.current;
    const ok = n >= 3;
    setStage("mask", { status: ok ? "pass" : "fail", detail: `${n} masks in ${Math.round(performance.now() - t0)} ms`, data: { masks: n } });
    return ok;
  };

  const stageComposite = async () => {
    setStage("comp", { status: "running", detail: "Compositing onto visible canvas…" });
    const vid = hiddenVidRef.current;
    const W = vid.videoWidth || 720;
    const H = vid.videoHeight || 1280;
    const master = masterCanvasRef.current;
    master.width = W; master.height = H;
    const ctx = master.getContext("2d");
    const mc = maskCanvasRef.current;
    if (mc) { mc.width = W; mc.height = H; }
    const det = document.createElement("canvas");
    det.width = W; det.height = H;
    const dctx = det.getContext("2d");
    detachedCanvasRef.current = det;
    const sub = document.createElement("canvas");
    sub.width = W; sub.height = H;
    const sctx = sub.getContext("2d");
    drawCountRef.current = 0;
    haveMaskRef.current = false;

    const loop = () => {
      if (stoppedRef.current) return;
      if (vid.readyState >= 2) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 1;
        ctx.filter = "blur(20px) brightness(0.85)";
        ctx.drawImage(vid, -20, -20, W + 40, H + 40);
        ctx.filter = "none";
        if (haveMaskRef.current && mc) {
          ctx.globalCompositeOperation = "destination-out";
          ctx.drawImage(mc, 0, 0, W, H);
          ctx.globalCompositeOperation = "source-over";
          sctx.globalCompositeOperation = "source-over";
          sctx.filter = "none";
          sctx.clearRect(0, 0, W, H);
          sctx.drawImage(vid, 0, 0, W, H);
          sctx.globalCompositeOperation = "destination-in";
          sctx.filter = "blur(6px)";
          sctx.drawImage(mc, 0, 0, W, H);
          sctx.filter = "none";
          sctx.globalCompositeOperation = "source-over";
          ctx.drawImage(sub, 0, 0);
        }
        if (segRef.current && !segBusyRef.current) {
          segBusyRef.current = true;
          try { segRef.current.send({ image: vid }).catch(() => { segBusyRef.current = false; }); }
          catch { segBusyRef.current = false; }
        }
        dctx.clearRect(0, 0, W, H);
        dctx.drawImage(master, 0, 0);
        drawCountRef.current++;
      }
      compositeRafRef.current = requestAnimationFrame(loop);
    };
    loop();
    await sleep(2500);
    const draws = drawCountRef.current;
    const px = samplePixels(master);
    const ok = draws >= 20 && px > 2;
    setStage("comp", {
      status: ok ? "pass" : "fail",
      detail: `${draws} draws/2.5s · canvas brightness ${px} · mask=${haveMaskRef.current ? "applied" : "NOT applied"}`,
      data: { draws, px, maskApplied: haveMaskRef.current },
    });
    return ok;
  };

  const runCaptureTest = async (key, canvas, videoEl) => {
    setStage(key, { status: "running", detail: "captureStream → <video>…" });
    try {
      const stream = canvas.captureStream(30);
      const track = stream.getVideoTracks()[0];
      try { track.contentHint = "motion"; } catch {}
      const events = [];
      track.addEventListener("mute", () => events.push("mute"));
      track.addEventListener("unmute", () => events.push("unmute"));
      videoEl.srcObject = stream;
      videoEl.muted = true; videoEl.playsInline = true;
      await videoEl.play().catch((e) => events.push(`play-rejected:${e?.name}`));
      await sleep(700);
      const frames = await countFrames(videoEl, 3000);
      const px = samplePixels(videoEl);
      const ok = frames >= 10 && px > 2;
      const warn = frames >= 10 && px <= 2;
      setStage(key, {
        status: ok ? "pass" : warn ? "warn" : "fail",
        detail: `${frames} frames/3s · brightness ${px} · track=${track.readyState}${track.muted ? "/MUTED" : ""} · ${videoEl.videoWidth}×${videoEl.videoHeight}${events.length ? " · ev:" + events.join(",") : ""}`,
        data: { frames, px, trackState: track.readyState, trackMuted: track.muted, events, vw: videoEl.videoWidth, vh: videoEl.videoHeight },
      });
      return { ok, frames, px };
    } catch (e) {
      setStage(key, { status: "fail", detail: `captureStream failed: ${e?.message || e}`, data: { error: String(e) } });
      return { ok: false, frames: 0, px: -1 };
    }
  };

  const stageResize = async () => {
    setStage("resize", { status: "running", detail: "Capture → resize canvas → still alive?" });
    try {
      const c = resizeCanvasRef.current;
      c.width = 320; c.height = 240;
      const cx = c.getContext("2d");
      let hue = 0;
      const anim = () => {
        if (stoppedRef.current) return;
        hue = (hue + 4) % 360;
        cx.fillStyle = `hsl(${hue},80%,50%)`;
        cx.fillRect(0, 0, c.width, c.height);
        cx.fillStyle = "#fff";
        cx.font = "20px monospace";
        cx.fillText(String(Date.now() % 100000), 20, 40);
        resizeRafRef.current = requestAnimationFrame(anim);
      };
      anim();
      const stream = c.captureStream(30);
      const track = stream.getVideoTracks()[0];
      const v = document.createElement("video");
      v.muted = true; v.playsInline = true;
      v.style.cssText = "position:fixed;left:-9999px;width:64px;height:48px;";
      document.body.appendChild(v);
      v.srcObject = stream;
      await v.play().catch(() => {});
      await sleep(400);
      const before = await countFrames(v, 1500);
      // THE probe — production hook resizes the canvas AFTER captureStream.
      c.width = 480; c.height = 360;
      await sleep(300);
      const after = await countFrames(v, 1500);
      const pxAfter = samplePixels(v);
      v.remove();
      const survived = after >= 5 && pxAfter > 2;
      setStage("resize", {
        status: survived ? "pass" : "fail",
        detail: `before-resize ${before} f/1.5s → after-resize ${after} f/1.5s · brightness ${pxAfter} · track=${track.readyState}${track.muted ? "/MUTED" : ""}`,
        data: { before, after, pxAfter, trackMuted: track.muted },
      });
      return survived;
    } catch (e) {
      setStage("resize", { status: "fail", detail: String(e?.message || e), data: { error: String(e) } });
      return false;
    }
  };

  const stageHook = async () => {
    setStage("hook", { status: "running", detail: "Mounting real useGreenScreen…" });
    hookInfoRef.current = {};
    setHookActive(true);
    // Give the hook time to boot MediaPipe (already CDN-cached from stage 3) + emit a track.
    await sleep(3500);
    const info = hookInfoRef.current || {};
    const v = hookVideoRef.current;
    const frames = await countFrames(v, 3000);
    const px = samplePixels(v);
    const ok = !!info.trackId && frames >= 10 && px > 2;
    setStage("hook", {
      status: ok ? "pass" : "fail",
      detail: `track=${info.trackId ? info.trackId.slice(0, 6) : "NULL"}${info.trackMuted ? "/MUTED" : ""} · seg=${info.segStatus || "?"} ready=${info.ready ? "Y" : "N"} · ${frames} frames/3s · brightness ${px} · ${v?.videoWidth || 0}×${v?.videoHeight || 0}`,
      data: { ...info, frames, px, vw: v?.videoWidth, vh: v?.videoHeight },
    });
    return ok;
  };

  // ---------- Verdict engine ----------
  const buildVerdicts = (st) => {
    const out = [];
    const s = (k) => st[k]?.status;
    const d = (k) => st[k]?.data || {};
    if (s("cam") === "fail") out.push("Camera itself failed — nothing downstream can work. Check browser permissions.");
    if (s("hidden") === "fail") out.push("Hidden <video> elements don't decode on this browser — the compositor never gets pixels. Fix: feed the compositor from the visible preview video instead of a hidden one.");
    if (s("mp") === "fail") out.push("MediaPipe CDN is blocked/unsupported here — AI cutout can never work. Fix: switch to @mediapipe/tasks-vision bundled locally.");
    if (s("mask") === "fail" && s("mp") === "pass") out.push("MediaPipe loads but produces no masks — model inference is failing on this device. Fix: switch to @mediapipe/tasks-vision.");
    if (s("comp") === "fail") out.push("The canvas composite itself isn't drawing — check the visible canvas above.");
    if (s("comp") === "pass" && !d("comp").maskApplied) out.push("Composite draws but the AI mask never got applied — cutout falls back to blur-only.");
    if (s("capdet") === "fail" && s("capatt") === "pass") out.push("ROOT CAUSE: captureStream from a DETACHED canvas doesn't render on this browser (known WebKit bug). The production hook uses a detached canvas. Fix: attach the compositor canvas to the DOM (or render the canvas directly as the preview).");
    if (s("capdet") === "fail" && s("capatt") === "fail") out.push("ROOT CAUSE: canvas.captureStream doesn't render into <video> on this browser at all. Fix: display the compositor canvas directly as the preview, and only use captureStream for the outbound WebRTC track.");
    if (s("resize") === "fail") out.push("ROOT CAUSE: resizing a canvas AFTER captureStream kills the stream on this browser. The production hook creates the canvas at 1920×1080 then resizes to the camera's real size — that resize murders the track. Fix: create the canvas at the camera's native size BEFORE calling captureStream, never resize.");
    if (s("hook") === "fail" && s("capdet") === "pass" && s("resize") === "pass") out.push("The isolated pipeline works but the real hook fails — the bug is in useGreenScreen's internal wiring, not the browser.");
    if (s("hook") === "pass") out.push("The real hook works on this device — the bug is in how MobileLiveStudio / LiveBroadcaster consume the track (srcObject wiring), not the compositor.");
    if (!out.length) out.push("All stages passed — the compositor pipeline is healthy on this device. The bug must be in studio state wiring (GS toggle, prefs, or track hand-off).");
    return out;
  };

  const buildReport = (st, v) => ({
    ts: new Date().toISOString(),
    ua: navigator.userAgent,
    platform: {
      vendor: navigator.vendor,
      screen: `${window.screen?.width}×${window.screen?.height}`,
      window: `${window.innerWidth}×${window.innerHeight}`,
      dpr: window.devicePixelRatio,
      touch: "ontouchstart" in window,
      rvfc: typeof HTMLVideoElement !== "undefined" && "requestVideoFrameCallback" in HTMLVideoElement.prototype,
    },
    gsPrefs: (() => { try { return JSON.parse(localStorage.getItem("tapin_greenscreen_settings_v3") || "null"); } catch { return null; } })(),
    stages: Object.fromEntries(Object.entries(st).map(([k, x]) => [k, { status: x.status, detail: x.detail, data: x.data }])),
    verdicts: v,
  });

  // ---------- Runner ----------
  const runAll = async () => {
    cleanupAll();
    stoppedRef.current = false;
    setStages(freshStages());
    stagesRef.current = freshStages();
    setVerdicts([]);
    setDone(false);
    setReportId(null);
    setRunning(true);

    const stream = await stageCamera();
    let hiddenOk = false;
    if (stream) hiddenOk = await stageHidden(stream);
    else setStage("hidden", { status: "skip", detail: "Skipped — no camera" });

    const mpOk = await stageMediaPipe();

    if (stream && hiddenOk && mpOk) await stageMask();
    else setStage("mask", { status: "skip", detail: "Skipped — prerequisites failed" });

    let compOk = false;
    if (stream && hiddenOk) compOk = await stageComposite();
    else setStage("comp", { status: "skip", detail: "Skipped — no camera feed" });

    if (compOk) {
      await runCaptureTest("capdet", detachedCanvasRef.current, capDetVideoRef.current);
      await runCaptureTest("capatt", masterCanvasRef.current, capAttVideoRef.current);
    } else {
      setStage("capdet", { status: "skip", detail: "Skipped — composite failed" });
      setStage("capatt", { status: "skip", detail: "Skipped — composite failed" });
    }

    await stageResize();

    if (stream) await stageHook();
    else setStage("hook", { status: "skip", detail: "Skipped — no camera" });

    const finalStages = stagesRef.current;
    const v = buildVerdicts(finalStages);
    setVerdicts(v);
    setRunning(false);
    setDone(true);
  };

  const copyReport = async () => {
    const rep = buildReport(stagesRef.current, verdicts);
    try {
      await navigator.clipboard.writeText(JSON.stringify(rep, null, 2));
      toast.success("Report copied — paste it in the chat");
    } catch {
      toast.error("Couldn't copy — use Upload instead");
    }
  };

  const uploadReport = async () => {
    const rep = buildReport(stagesRef.current, verdicts);
    try {
      const { data } = await api.post("/greenscreen/diag", { report: rep });
      setReportId(data?.id || "ok");
      toast.success("Report uploaded — just say 'uploaded' in the chat");
    } catch (e) {
      toast.error("Upload failed — use Copy instead");
    }
  };

  return (
    <div data-testid="gs-lab-page" className="min-h-screen bg-[#070B14] text-white pb-28">
      <div className="max-w-lg mx-auto px-4 pt-24">
        <div className="flex items-center gap-2 mb-1">
          <Camera className="w-5 h-5 text-emerald-400" />
          <h1 className="text-xl font-black tracking-tight">Green Screen Diagnostic Lab</h1>
        </div>
        <p className="text-white/50 text-sm mb-5">
          Tests all 9 stages of the AI background pipeline on <b>this device</b>. Allow camera access, keep your face in view, then upload the report.
        </p>

        <button
          type="button"
          onClick={runAll}
          disabled={running}
          data-testid="gs-lab-run-btn"
          className={`w-full rounded-full py-3.5 font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2 mb-6 transition ${
            running ? "bg-white/10 text-white/40" : "bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_25px_rgba(16,185,129,0.4)]"
          }`}
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? "Running diagnostics…" : done ? "Run again" : "Run diagnostics"}
        </button>

        {/* Stage list */}
        <div className="space-y-2 mb-6">
          {STAGE_DEFS.map(({ key, label }) => {
            const st = stages[key];
            return (
              <div
                key={key}
                data-testid={`gs-lab-stage-${key}`}
                className={`rounded-xl border px-3 py-2.5 flex items-start gap-2.5 ${
                  st.status === "pass" ? "border-emerald-500/40 bg-emerald-500/5"
                  : st.status === "fail" ? "border-red-500/50 bg-red-500/10"
                  : st.status === "warn" ? "border-amber-500/50 bg-amber-500/10"
                  : st.status === "running" ? "border-cyan-500/40 bg-cyan-500/5"
                  : "border-white/10 bg-white/[0.03]"
                }`}
              >
                <span className="mt-0.5 shrink-0">
                  {st.status === "pass" && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  {st.status === "fail" && <XCircle className="w-4 h-4 text-red-400" />}
                  {st.status === "warn" && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                  {st.status === "running" && <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />}
                  {st.status === "skip" && <MinusCircle className="w-4 h-4 text-white/30" />}
                  {st.status === "pending" && <MinusCircle className="w-4 h-4 text-white/20" />}
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold leading-tight">{label}</div>
                  {st.detail && <div className="text-[11px] text-white/55 font-mono leading-snug break-words">{st.detail}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Verdict */}
        {done && (
          <div data-testid="gs-lab-verdict" className="rounded-xl border border-fuchsia-500/40 bg-fuchsia-500/10 px-4 py-3 mb-6">
            <div className="text-xs font-black uppercase tracking-widest text-fuchsia-300 mb-1.5">Verdict</div>
            <ul className="space-y-1.5">
              {verdicts.map((v, i) => (
                <li key={i} className="text-[13px] leading-snug text-white/90">• {v}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Report actions */}
        {done && (
          <div className="flex gap-2 mb-8">
            <button type="button" onClick={uploadReport} data-testid="gs-lab-upload-btn"
              className="flex-1 rounded-full py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-black uppercase tracking-wider text-xs flex items-center justify-center gap-1.5">
              <UploadCloud className="w-4 h-4" /> Upload report
            </button>
            <button type="button" onClick={copyReport} data-testid="gs-lab-copy-btn"
              className="flex-1 rounded-full py-3 bg-white/10 hover:bg-white/20 font-black uppercase tracking-wider text-xs flex items-center justify-center gap-1.5">
              <Copy className="w-4 h-4" /> Copy JSON
            </button>
          </div>
        )}
        {reportId && (
          <div data-testid="gs-lab-report-id" className="text-center text-emerald-400 text-xs font-mono mb-6">
            ✓ Uploaded — report id {reportId}
          </div>
        )}

        {/* Preview grid */}
        <div className="text-xs font-black uppercase tracking-widest text-white/40 mb-2">Live previews</div>
        <div className="grid grid-cols-2 gap-2">
          <PreviewCell label="1 · Camera">
            <video ref={camVideoRef} autoPlay playsInline muted data-testid="gs-lab-video-cam" className="w-full h-full object-cover" />
          </PreviewCell>
          <PreviewCell label="5 · Composite canvas">
            <canvas ref={masterCanvasRef} data-testid="gs-lab-canvas-master" className="w-full h-full object-cover" />
          </PreviewCell>
          <PreviewCell label="6 · Detached capture">
            <video ref={capDetVideoRef} autoPlay playsInline muted data-testid="gs-lab-video-capdet" className="w-full h-full object-cover" />
          </PreviewCell>
          <PreviewCell label="7 · Attached capture">
            <video ref={capAttVideoRef} autoPlay playsInline muted data-testid="gs-lab-video-capatt" className="w-full h-full object-cover" />
          </PreviewCell>
          <PreviewCell label="8 · Resize probe">
            <canvas ref={resizeCanvasRef} data-testid="gs-lab-canvas-resize" className="w-full h-full object-cover" />
          </PreviewCell>
          <PreviewCell label="9 · Real hook output">
            <video ref={hookVideoRef} autoPlay playsInline muted data-testid="gs-lab-video-hook" className="w-full h-full object-cover" />
          </PreviewCell>
        </div>
      </div>

      {hookActive && camStream && (
        <HookProbe
          stream={camStream}
          videoElRef={hookVideoRef}
          onUpdate={(u) => { hookInfoRef.current = u; }}
        />
      )}
    </div>
  );
}

function PreviewCell({ label, children }) {
  return (
    <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
      <div className="aspect-[3/4]">{children}</div>
      <div className="px-2 py-1 text-[10px] font-bold text-white/60 bg-white/[0.04]">{label}</div>
    </div>
  );
}
